<template>
  <main class="mobile-page release-page">
    <div class="release-backdrop" aria-hidden="true"></div>

    <div class="mobile-screen release-shell">
      <header class="mobile-topnav">
        <RouterLink class="mobile-icon-btn" to="/mobile" aria-label="返回首页">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><path d="M14.5 5.5 8 12l6.5 6.5" /></svg>
        </RouterLink>
        <span class="mobile-topnav-title">官方发布</span>
        <RouterLink class="mobile-icon-btn release-register-link" to="/mobile/register">注册</RouterLink>
      </header>

      <section class="release-hero">
        <div class="app-mark">
          <img :src="logo" alt="点指之间" />
          <span aria-hidden="true"></span>
        </div>
        <span class="mobile-eyebrow">Official Release · 官方渠道</span>
        <h1>{{ justRegistered ? '注册成功' : '下载点指之间' }}</h1>
        <p>{{ justRegistered ? '安装客户端，使用刚注册的手机号登录' : '安装客户端，开启你的连接与价值网络' }}</p>
        <div v-if="release.versionName" class="version-pill">
          <span>最新版本</span>
          <strong>{{ release.versionName }}</strong>
        </div>
      </section>

      <section v-if="loading" class="mobile-card state-card">
        <van-loading color="#62f4d6" />
        <strong>正在获取最新版本</strong>
        <p>请保持网络连接，稍候片刻。</p>
      </section>

      <section v-else-if="error" class="mobile-card state-card">
        <strong>暂时无法获取版本</strong>
        <p>请检查网络后重试，或联系官方客服。</p>
        <button class="mobile-btn mobile-btn-primary" type="button" @click="loadRelease">重新获取</button>
      </section>

      <section v-else-if="!isDesktop && !hasDownloadChannel" class="mobile-card state-card">
        <strong>{{ unavailableTitle }}</strong>
        <p>{{ unavailableText }}</p>
        <RouterLink class="mobile-btn mobile-btn-ghost" to="/mobile">返回移动官网</RouterLink>
      </section>

      <template v-else>
        <div v-if="justRegistered" class="mobile-card registered-card">
          <span>✓</span>
          <p>账号已创建成功，下载后可直接登录 APP。</p>
        </div>

        <button
          v-if="primaryChannel"
          class="primary-download"
          type="button"
          @click="openUrl(primaryChannel.url)"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3v12m-5-5 5 5 5-5M5 20h14" /></svg>
          <span>
            <b>{{ primaryChannel.title }}</b>
            <small>{{ primaryChannel.description }}</small>
          </span>
        </button>

        <section v-if="otherStores.length" class="store-section">
          <div class="section-heading"><span>更多应用商店</span><i></i></div>
          <div class="store-list">
            <button v-for="store in otherStores" :key="store.key" type="button" @click="openStore(store)">
              <span class="store-icon">{{ store.label?.slice(0, 1) }}</span>
              <span>
                <b>{{ store.label }}</b>
                <small>前往应用商店</small>
              </span>
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7" /></svg>
            </button>
          </div>
        </section>

        <section v-if="release.updateContent" class="mobile-card update-card">
          <span class="mobile-eyebrow title">Update · 更新内容</span>
          <p>{{ release.updateContent }}</p>
        </section>

        <section v-if="isDesktop" class="mobile-card desktop-share">
          <b>手机扫码下载</b>
          <div class="qr-frame">
            <img v-if="qrDataUrl" :src="qrDataUrl" alt="点指之间移动下载页二维码" />
            <span v-else>{{ qrError ? '二维码生成失败' : '正在生成二维码' }}</span>
          </div>
          <p>{{ shareUrl }}</p>
          <button class="mobile-btn mobile-btn-ghost" type="button" @click="copyShareUrl">复制下载链接</button>
        </section>
      </template>

      <p class="security">官方发布渠道 · 安装包持续安全检测</p>
    </div>

    <div v-if="restricted" class="browser-guide" @click="restricted = false">
      <div class="guide-arrow" aria-hidden="true">↗</div>
      <p><b>请在系统浏览器中打开</b><span>点击右上角菜单，选择“在浏览器打开”后继续下载</span></p>
      <button type="button">我知道了</button>
    </div>
  </main>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { useRoute } from 'vue-router'
import { showToast } from 'vant'
import logo from '@/assets-mobile/images/logo.png'
import { getDownloadInfo } from '@/mobile-api/auth'
import { getOsPlatform, isMobileDevice } from '@/device/platform'
import { getDownloadShareUrl } from '@/utils/site-url'

const route = useRoute()
const loading = ref(true)
const error = ref(false)
const restricted = ref(false)
const qrDataUrl = ref('')
const qrError = ref(false)
const justRegistered = ref(false)
const release = reactive({ apkUrl: '', versionName: '', updateContent: '', packageSize: '', storeList: [] })
const ua = navigator.userAgent || ''
const osPlatform = getOsPlatform()
const isAndroid = osPlatform === 'android'
const isIos = osPlatform === 'ios'
const isDesktop = !isMobileDevice()
const isEmbedded = /MicroMessenger|QQ\//i.test(ua) || /wxwork/i.test(ua)
const shareUrl = getDownloadShareUrl()
const availableStores = computed(() => (release.storeList || [])
  .map((store) => {
    const packageName = store.packageName || 'cn.vitaeai.dzzj'
    const templateUrl = store.uriTemplate?.replace('%s', encodeURIComponent(packageName))
    const url = store.webUrl || templateUrl || store.intentUrl
    return url ? { ...store, url } : null
  })
  .filter(Boolean))
const recommendedStore = computed(() => availableStores.value[0])
const otherStores = computed(() => availableStores.value.slice(1))
const primaryChannel = computed(() => {
  if (isDesktop) return null
  if (recommendedStore.value) {
    return {
      url: recommendedStore.value.url,
      title: `${recommendedStore.value.label || '应用商店'}下载`,
      description: '推荐官方应用商店 · 安全检测'
    }
  }
  if (isAndroid && release.apkUrl) {
    return {
      url: release.apkUrl,
      title: '下载 Android 安装包',
      description: release.packageSize ? `官方 APK · ${release.packageSize}` : '官方 APK · 安全下载'
    }
  }
  return null
})
const hasDownloadChannel = computed(() => Boolean(primaryChannel.value))
const unavailableTitle = computed(() => isIos ? 'iOS 版本暂未开放' : '新版本即将上线')
const unavailableText = computed(() => isIos ? '当前 iOS 渠道暂未发布，请先关注官方通知。' : '当前没有可公开下载的安装包，请稍后再来。')

async function loadRelease() {
  loading.value = true
  error.value = false
  try {
    const result = await getDownloadInfo()
    Object.assign(release, result.data || result || {})
    release.storeList = release.storeList || []
  } catch {
    error.value = true
  } finally {
    loading.value = false
  }
}

function openStore(store) {
  openUrl(store.url)
}

function openUrl(url) {
  if (!url) return
  if (isEmbedded) {
    restricted.value = true
    return
  }
  window.location.href = url
}

async function copyShareUrl() {
  try {
    await navigator.clipboard.writeText(shareUrl)
    showToast('下载链接已复制')
  } catch {
    window.prompt('请复制下载链接', shareUrl)
  }
}

async function generateQrCode() {
  if (!isDesktop) return
  try {
    const { toDataURL } = await import('qrcode')
    qrDataUrl.value = await toDataURL(shareUrl, {
      width: 210,
      margin: 1,
      color: { dark: '#02050d', light: '#ffffff' }
    })
  } catch {
    qrError.value = true
  }
}

onMounted(() => {
  if (route.query.from === 'register' && sessionStorage.getItem('dzzj_register_success')) {
    justRegistered.value = true
    sessionStorage.removeItem('dzzj_register_success')
  }
  loadRelease()
  generateQrCode()
})
</script>

<style scoped>
.release-page { position:relative; overflow:hidden; padding-bottom:calc(36px + env(safe-area-inset-bottom)); }
.release-backdrop { position:absolute; inset:0; pointer-events:none; background:repeating-linear-gradient(to bottom, transparent 0, transparent 5px, rgba(98,244,214,.035) 6px); opacity:.8; }
.release-shell { position:relative; max-width:430px; margin:0 auto; padding-bottom:0; }
.release-register-link { width:auto; flex:0 0 auto; padding:0 11px; color:var(--mobile-cyan); font-size:12px; font-weight:800; text-decoration:none; }
.release-hero { display:flex; flex-direction:column; align-items:center; padding:26px 0 24px; text-align:center; }
.app-mark { position:relative; width:88px; margin:0 auto 18px; }
.app-mark img { display:block; width:88px; height:88px; border-radius:22px; box-shadow:0 20px 48px rgba(0,0,0,.44); }
.app-mark span { position:absolute; right:-2px; bottom:4px; width:15px; height:15px; border:3px solid #071523; border-radius:50%; background:var(--mobile-cyan); box-shadow:0 0 12px rgba(98,244,214,.8); }
.release-hero h1 { margin:12px 0 8px; font-size:30px; line-height:1.2; font-weight:900; }
.release-hero > p { max-width:300px; color:var(--mobile-muted); font-size:12px; line-height:1.7; }
.version-pill { display:inline-flex; align-items:center; gap:7px; margin-top:15px; padding:6px 11px; border:1px solid rgba(98,244,214,.28); border-radius:999px; background:rgba(98,244,214,.08); }
.version-pill span { color:var(--mobile-quiet); font-size:10px; font-weight:700; }
.version-pill strong { color:var(--mobile-cyan); font-size:11px; font-weight:900; }
.state-card { display:flex; flex-direction:column; align-items:center; text-align:center; }
.state-card strong { margin-top:10px; font-size:19px; font-weight:900; }
.state-card p { margin:8px 0 16px; color:var(--mobile-muted); font-size:12px; line-height:1.7; }
.state-card .mobile-btn { width:100%; }
.registered-card { display:flex; align-items:center; gap:10px; margin-bottom:12px; padding:13px 14px; }
.registered-card span { display:grid; place-items:center; width:26px; height:26px; flex:none; border-radius:50%; color:var(--mobile-cta-ink); background:var(--mobile-cyan); font-size:13px; font-weight:900; }
.registered-card p { color:var(--mobile-muted); font-size:12px; line-height:1.6; }
.primary-download { width:100%; min-height:68px; display:flex; align-items:center; justify-content:center; gap:13px; margin-top:4px; border-radius:15px; color:var(--mobile-cta-ink); background:linear-gradient(135deg,#78f3d8,#46d6b6 58%,#2fc4a4); box-shadow:0 16px 36px rgba(70,214,182,.26), inset 0 1px 0 rgba(255,255,255,.35); text-align:left; }
.primary-download:active { transform:translateY(1px); }
.primary-download > svg { width:25px; flex:none; fill:none; stroke:currentColor; stroke-width:2; stroke-linecap:round; stroke-linejoin:round; }
.primary-download span { display:grid; gap:3px; min-width:0; }
.primary-download b { font-size:15px; font-weight:900; }
.primary-download small { font-size:10px; font-weight:700; opacity:.72; }
.store-section { margin-top:18px; }
.section-heading { display:flex; align-items:center; gap:12px; margin-bottom:9px; color:var(--mobile-quiet); font-size:11px; font-weight:800; }
.section-heading i { height:1px; flex:1; background:var(--mobile-line); }
.store-list { overflow:hidden; border:1px solid var(--mobile-line); border-radius:14px; background:rgba(9,18,33,.72); }
.store-list button { width:100%; min-height:62px; display:grid; grid-template-columns:36px 1fr 20px; align-items:center; gap:11px; padding:9px 13px; color:var(--mobile-ink); background:transparent; text-align:left; }
.store-list button + button { border-top:1px solid var(--mobile-line); }
.store-icon { width:36px; height:36px; display:grid; place-items:center; border:1px solid rgba(255,207,106,.3); border-radius:10px; color:var(--mobile-gold); background:rgba(255,207,106,.08); font-weight:900; }
.store-list button > span:nth-child(2) { display:grid; gap:2px; min-width:0; }
.store-list b { font-size:13px; font-weight:800; }
.store-list small { color:var(--mobile-quiet); font-size:10px; }
.store-list svg { width:18px; fill:none; stroke:var(--mobile-quiet); stroke-width:1.8; }
.update-card { margin-top:14px; }
.update-card p { margin-top:9px; color:var(--mobile-muted); font-size:11.5px; line-height:1.75; white-space:pre-line; }
.desktop-share { margin-top:14px; text-align:center; }
.desktop-share b { font-size:14px; font-weight:900; }
.qr-frame { width:186px; height:186px; display:grid; place-items:center; margin:13px auto; padding:8px; border:1px solid var(--mobile-line-strong); border-radius:16px; background:#fff; }
.qr-frame img { width:100%; height:100%; }
.qr-frame span { color:#526b80; font-size:11px; }
.desktop-share p { margin:0 0 12px; color:var(--mobile-quiet); font-size:10.5px; word-break:break-all; }
.browser-guide { position:fixed; inset:0; z-index:80; display:flex; flex-direction:column; align-items:center; padding:calc(env(safe-area-inset-top) + 70px) 30px; background:rgba(2,9,16,.95); text-align:center; cursor:pointer; }
.guide-arrow { align-self:flex-end; margin-right:4px; color:var(--mobile-cyan); font-size:52px; line-height:1; }
.browser-guide p { display:grid; gap:10px; margin-top:25px; }
.browser-guide b { font-size:21px; font-weight:900; }
.browser-guide span { color:var(--mobile-muted); font-size:13px; line-height:1.7; }
.browser-guide button { margin-top:30px; padding:11px 28px; border-radius:11px; color:var(--mobile-cta-ink); background:var(--mobile-cyan); font-weight:900; }
.security { padding:24px 0 0; color:var(--mobile-faint); font-size:10px; text-align:center; }
</style>
